import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const galleryItems = [
  {
    id: 1,
    title: 'Fountains',
    category: 'Fountains',
    type: 'video',
    videoEmbed: 'https://embed.wave.video/bu23Z5pPIaO1PjjV',
    thumbnail: 'https://customer-assets.emergentagent.com/job_yard-care-expert/artifacts/flvn01dc_Fountain_IMG_1272.png',
    description: 'Beautiful fountain installation showcasing water features.'
  },
  {
    id: 2,
    title: 'Residential Irrigation System',
    category: 'Irrigation',
    type: 'image',
    image: 'https://images.unsplash.com/photo-1770664945615-52203ab54c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxzcHJpbmtsZXIlMjBpcnJpZ2F0aW9uJTIwc3lzdGVtJTIwd2F0ZXJpbmclMjBncmVlbiUyMGxhd258ZW58MHx8fHwxNzcxOTQ2NDMzfDA&ixlib=rb-4.1.0&q=85',
    description: 'Complete sprinkler system installation for a 2-acre residential property.'
  },
  {
    id: 3,
    title: 'Garden Path Lighting',
    category: 'Lighting',
    type: 'image',
    image: 'https://images.unsplash.com/photo-1764936509889-a92ac04d9be0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxsYW5kc2NhcGUlMjBsaWdodGluZyUyMHBhdGglMjBsaWdodHMlMjBuaWdodCUyMGdhcmRlbnxlbnwwfHx8fDE3NzE5NDY0MzR8MA&ixlib=rb-4.1.0&q=85',
    description: 'LED pathway lighting installation enhancing nighttime garden beauty.'
  },
  {
    id: 4,
    title: 'Landscape Blueprint Design',
    category: 'Design',
    type: 'image',
    image: 'https://images.unsplash.com/photo-1712697236445-fad56534129e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxsYW5kc2NhcGUlMjBibHVlcHJpbnRzJTIwYXJjaGl0ZWN0dXJhbCUyMGRyYXdpbmd8ZW58MHx8fHwxNzcxOTQ2NDM2fDA&ixlib=rb-4.1.0&q=85',
    description: 'Custom CAD irrigation blueprint for a new construction project.'
  },
  {
    id: 5,
    title: 'Evening Landscape Ambiance',
    category: 'Lighting',
    type: 'image',
    image: 'https://images.pexels.com/photos/34868979/pexels-photo-34868979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    description: 'Accent lighting creating a stunning nighttime outdoor atmosphere.'
  },
  {
    id: 6,
    title: 'Smart Irrigation Controller',
    category: 'Technology',
    type: 'image',
    image: 'https://customer-assets.emergentagent.com/job_yard-care-expert/artifacts/dlkx85ae_sprinklersmartcontroller.jpg',
    description: 'WiFi-enabled smart controller installation for water efficiency.'
  }
];

const categories = ['All', 'Irrigation', 'Lighting', 'Design', 'Technology', 'Fountains'];

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const filteredItems = selectedCategory === 'All' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  const openLightbox = (item, index) => {
    setSelectedImage(item);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % filteredItems.length
      : (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredItems[newIndex]);
  };

  return (
    <div className="pt-24 md:pt-28" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-mist-dark" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-6">
              Our Work
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-mist-white mb-6">
              Project <span className="text-mist-light">Gallery</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-mist-light/80 max-w-2xl mx-auto">
              Browse our portfolio of completed irrigation, lighting, and landscape projects. 
              See the quality of our work firsthand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Category Filter */}
          <motion.div {...fadeUp} className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-body font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-mist-blue text-white'
                    : 'bg-mist-light text-mist-dark hover:bg-mist-light/80'
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Gallery Grid */}
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode="popLayout">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card 
                    className="group bg-white border-0 shadow-lg rounded-2xl overflow-hidden cursor-pointer card-hover"
                    onClick={() => openLightbox(item, index)}
                    data-testid={`gallery-item-${item.id}`}
                  >
                    <div className="relative image-zoom">
                      {item.type === 'video' ? (
                        <div className="w-full h-64 relative">
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-mist-dark/60 to-mist-dark/20 flex items-center justify-center">
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center mb-2 group-hover:bg-white group-hover:scale-110 transition-all duration-300">
                                <svg className="w-8 h-8 text-landscape-green ml-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                              <span className="text-white font-body text-sm">Click to play video</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-64 object-cover"
                          />
                          <div className="absolute inset-0 bg-mist-dark/0 group-hover:bg-mist-dark/40 transition-colors duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-mist-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <span className="inline-block px-3 py-1 bg-mist-light text-mist-blue text-xs font-body font-medium rounded-full mb-3">
                        {item.category}
                      </span>
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-2">
                        {item.title}
                      </h3>
                      <p className="font-body text-sm text-mist-blue">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <button
              className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            
            <button
              className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedImage.type === 'video' ? (
                <div className="relative mx-auto" style={{ width: '40vh', height: '70vh' }}>
                  <iframe 
                    src={selectedImage.videoEmbed}
                    className="absolute inset-0 w-full h-full rounded-2xl"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    scrolling="no"
                    title={selectedImage.title}
                  />
                </div>
              ) : (
                <img
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-2xl"
                />
              )}
              <div className="text-center mt-6">
                <h3 className="font-heading text-2xl font-bold text-white mb-2">
                  {selectedImage.title}
                </h3>
                <p className="font-body text-white/70">
                  {selectedImage.description}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-landscape-green" data-testid="gallery-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Want Results Like These?
            </h2>
            <p className="font-body text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Let's discuss your project and create something beautiful for your property.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-landscape-green hover:bg-mist-white rounded-full px-8 py-6 text-lg font-medium"
                data-testid="gallery-cta-btn"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
